<template>
  <form @submit.prevent="beginProcess()">
    <div class="modal-card" style="width: auto">
      <header class="modal-card-head flex justify-between">
        <p class="modal-card-title">
          Verify Changes/Additions to FileMaker
        </p>
      </header>
      <section class="modal-card-body">
        <transition name="fade" mode="out-in">
          <div v-if="submitting" key="submitting">
            <div v-for="(step, index) in submissionSteps" :key="index">
              <div v-if="step.status === 'waiting'" class="flex items-center">
                <d-icon icon="clock" class="mr-3" /> {{ step.title }}
              </div>
              <div v-else-if="step.status === 'error'" class="flex items-center">
                <d-icon icon="times" class="mr-3" /> {{ step.title }}
              </div>
              <div v-else class="flex items-center">
                <d-icon icon="check" class="mr-3" /> {{ step.title }}
              </div>
            </div>
            <div v-if="processingErrorMessage" class="pt-5 text-error">
              An error has occurred: {{ processingErrorMessage }}
            </div>
          </div>
          <div v-else key="waiting">
            <p class="mb-8">
              Please review the following information that will be submitted to FileMaker. Press the "Go Back" button to make further changes, or update the information in FileMaker after approval has finalized. Once you press "Continue", please stay on this page while the information process.
            </p>
            <div class="mb-5">
              <p class="text-xl font-bold mb-2">
                {{ company['Organization ID'] ? 'Update' : 'New' }} Organization
              </p>
              <p><strong>Name</strong>: {{ company['Organization Name'] }}</p>
              <p><strong>Email</strong>: {{ company['Organization Email Address'] }}</p>
              <p><strong>Phone</strong>: {{ company.Phone }}</p>
              <p><strong>Fax</strong>: {{ company['Phone Fax'] }}</p>
              <p><strong>Address 1</strong>: {{ company['Address Line1'] }}</p>
              <p><strong>Address 2</strong>: {{ company['Address Line2'] }}</p>
              <p><strong>City</strong>: {{ company['Address City'] }}</p>
              <p><strong>State</strong>: {{ company['Address State'] }}</p>
              <p><strong>Zip/Postal</strong>: {{ company['Address Zip'] }}</p>
              <p><strong>Country</strong>: {{ company['Address Country'] }}</p>
              <p><strong>Website URL</strong>: {{ company.WebsiteURL }}</p>
            </div>
            <div class="mb-5">
              <p class="text-xl font-bold mb-2">
                {{ primaryContact['Contact ID'] ? 'Update' : 'New' }} Contact (Primary Contact)
              </p>
              <p><strong>First Name</strong>: {{ primaryContact['Name First Name'] }}</p>
              <p><strong>Last Name</strong>: {{ primaryContact['Name Last Name'] }}</p>
              <p><strong>Member Type</strong>: {{ primaryContact['Member Type'] }}</p>
              <p><strong>Job Title</strong>: {{ primaryContact['Job Title'] }}</p>
              <p><strong>Email</strong>: {{ primaryContact.Email }}</p>
              <p><strong>Phone</strong>: {{ primaryContact.Phone }}</p>
              <p><strong>Phone Fax</strong>: {{ primaryContact['Phone Fax'] }}</p>
              <p><strong>Is QCP Liaison?</strong>: {{ primaryContact['QCP liaison'] === '1' ? 'Yes' : 'No' }}</p>
              <p><strong>Is Billing Contact?</strong>: {{ primaryContact['Billing Contact'] === '1' ? 'Yes' : 'No' }}</p>
            </div>
            <div class="mb-5">
              <p class="text-xl font-bold mb-2">
                {{ principalContactOne['Contact ID'] ? 'Update' : 'New' }} Contact (Principal Contact #1)
              </p>
              <p><strong>First Name</strong>: {{ principalContactOne['Name First Name'] }}</p>
              <p><strong>Last Name</strong>: {{ principalContactOne['Name Last Name'] }}</p>
              <p><strong>Member Type</strong>: {{ principalContactOne['Member Type'] }}</p>
              <p><strong>Job Title</strong>: {{ principalContactOne['Job Title'] }}</p>
              <p><strong>Email</strong>: {{ principalContactOne.Email }}</p>
              <p><strong>Phone</strong>: {{ principalContactOne.Phone }}</p>
              <p><strong>Phone Fax</strong>: {{ principalContactOne['Phone Fax'] }}</p>
              <p><strong>Is QCP Liaison?</strong>: {{ principalContactOne['QCP liaison'] === '1' ? 'Yes' : 'No' }}</p>
              <p><strong>Is Billing Contact?</strong>: {{ principalContactOne['Billing Contact'] === '1' ? 'Yes' : 'No' }}</p>
            </div>
            <div class="mb-5">
              <p class="text-xl font-bold mb-2">
                {{ principalContactTwo['Contact ID'] ? 'Update' : 'New' }} Contact (Principal Contact #2)
              </p>
              <p><strong>First Name</strong>: {{ principalContactTwo['Name First Name'] }}</p>
              <p><strong>Last Name</strong>: {{ principalContactTwo['Name Last Name'] }}</p>
              <p><strong>Member Type</strong>: {{ principalContactTwo['Member Type'] }}</p>
              <p><strong>Job Title</strong>: {{ principalContactTwo['Job Title'] }}</p>
              <p><strong>Email</strong>: {{ principalContactTwo.Email }}</p>
              <p><strong>Phone</strong>: {{ principalContactTwo.Phone }}</p>
              <p><strong>Phone Fax</strong>: {{ principalContactTwo['Phone Fax'] }}</p>
              <p><strong>Is QCP Liaison?</strong>: {{ principalContactTwo['QCP liaison'] === '1' ? 'Yes' : 'No' }}</p>
              <p><strong>Is Billing Contact?</strong>: {{ principalContactTwo['Billing Contact'] === '1' ? 'Yes' : 'No' }}</p>
            </div>
          </div>
        </transition>
      </section>
      <footer class="modal-card-foot justify-end">
        <d-btn v-if="!submitting || processingErrorMessage" flat color="gray-800" @click="$parent.close()">
          Go Back
        </d-btn>
        <d-btn v-if="!processingErrorMessage" type="submit" color="success" :loading="submitting">
          Continue
        </d-btn>
      </footer>
    </div>
  </form>
</template>
<script>
  import dayjs from 'dayjs';
  import { update } from '@resources/scripts/services/api/applyForLicensing';
  import filemakerMapperService from '@resources/scripts/services/filemakerMapper.service';
  import { RequestStatus } from '@resources/scripts/models/enums';
  import { createOrg, queryFirstOrg, updateOrg } from '@resources/scripts/services/api/filemakerOrganizations.api';
  import { createTestContact } from '@resources/scripts/services/api/filemakerContactTests.api';
  import { createContact, updateContact, queryFirstContact } from '@resources/scripts/services/api/filemakerContacts.api';
  import axios from 'axios';

  export default {
    props: {
      submissionId: Number,
      submissionDetails: {
        type: Object,
        default: () => ({}),
      },
    },

    data: () => ({
      company: {},
      primaryContact: {},
      principalContactOne: {},
      principalContactTwo: {},
      projectOne: {},
      projectTwo: {},
      projectThree: {},
      submitting: false,
      processingErrorMessage: '',
      newSubmissionDetails: {},
      submissionSteps: [
        {
          title: 'Submit Organization',
          status: 'waiting',
        },
        {
          title: 'Submit Primary Contact',
          status: 'waiting',
        },
        {
          title: 'Submit Principal Contact #1',
          status: 'waiting',
        },
        {
          title: 'Submit Principal Contact #2',
          status: 'waiting',
        },
        {
          title: 'Create QCP Contact Test Information',
          status: 'waiting',
        },
        {
          title: 'Submit Transaction',
          status: 'waiting',
        },
        {
          title: 'Create User Account',
          status: 'waiting',
        },
      ],
    }),

    computed: {
      computedLicensingSections() {
        return JSON.parse(this.newSubmissionDetails.revisedLicensingSections);
      },
    },

    mounted() {
      this.newSubmissionDetails = JSON.parse(JSON.stringify(this.submissionDetails));
      this.company = filemakerMapperService.mapOrganizationSubmissionToFileMakerCreateOrganization(this.newSubmissionDetails.revisedCompany);
      this.primaryContact = filemakerMapperService.mapContactSubmissionToFileMakerCreateContact(this.newSubmissionDetails.revisedPrimaryContact, null, true, true);
      this.principalContactOne = filemakerMapperService.mapContactSubmissionToFileMakerCreateContact(this.newSubmissionDetails.revisedPrincipal1Contact);
      this.principalContactTwo = filemakerMapperService.mapContactSubmissionToFileMakerCreateContact(this.newSubmissionDetails.revisedPrincipal2Contact);
    },

    methods: {
      async beginProcess() {
        this.submitting = true;

        // Organization
        if (!this.company['Organization ID']) {
          // Submit Organization, if we didn't select it
          const { data: companyResult } = await createOrg(this.company);
          this.submissionSteps[0].status = 'completed';
          if (companyResult.messages[0].message !== 'OK') {
            this.submissionSteps[0].status = 'error';
            this.$toast.error('An error occurred while trying to create an organization');
            this.processingErrorMessage = companyResult.messages[0].message;
            return;
          }
          const { data: companyQueryResult } = await queryFirstOrg(filemakerMapperService.mapFileMakerCreateOrganizationToSafeQuery(this.company));
          this.company['Organization ID'] = companyQueryResult['Organization ID'];
          // Throw an error if the newly created organization can't be retrieved from FileMaker.
          if (!this.company['Organization ID']) {
            this.submissionSteps[0].status = 'error';
            this.$toast.error('An error occured while trying to retrieve the newly created organization.');
            this.processingErrorMessage = 'An error occured while trying to retrieve the newly created organization. Please close the modal and select the organization from the Similar Organization list.';
          }
        } else {
          // Update Organization, if we selected it
          await updateOrg(this.company['Organization ID'], this.company);
          this.submissionSteps[0].status = 'completed';
        }

        // Primary Contact
        this.primaryContact['Organization ID'] = this.company['Organization ID'];
        if (!this.primaryContact['Contact ID']) {
          // Submit Primary Contact, if we didn't select it
          const { data: primaryContactResult } = await createContact(this.primaryContact);
          this.submissionSteps[1].status = 'completed';
          if (primaryContactResult.messages[0].message !== 'OK') {
            this.submissionSteps[1].status = 'error';
            this.$toast.error('An error occurred while trying to create the primary contact');
            this.processingErrorMessage = primaryContactResult.messages[0].message;
            return;
          }
          const { data: primaryContactQueryResult } = await queryFirstContact({
            'Organization ID': this.primaryContact['Organization ID'],
            Email: `*${this.primaryContact.Email.substring(0, this.primaryContact.Email.indexOf('@'))}*`,
          });
          debugger;
          this.primaryContact['Contact ID'] = primaryContactQueryResult['Contact ID'];
        } else {
          // Update Primary Contact, if we selected it
          await updateContact(this.primaryContact['Contact ID'], this.primaryContact);
          this.submissionSteps[1].status = 'completed';
        }

        // Principal Contact #1
        this.principalContactOne['Organization ID'] = this.company['Organization ID'];
        if (!this.principalContactOne['Contact ID']) {
          // Submit Principal Contact #1, if we didn't select it
          const { data: principalContactOneResult } = await createContact(this.principalContactOne);
          this.submissionSteps[2].status = 'completed';
          if (principalContactOneResult.messages[0].message !== 'OK') {
            this.submissionSteps[2].status = 'error';
            this.$toast.error('An error occurred while trying to create the first principal contact');
            this.processingErrorMessage = principalContactOneResult.messages[0].message;
            return;
          }
        } else {
          // Update Principal Contact #1, if we selected it
          await updateContact(this.principalContactOne['Contact ID'], this.principalContactOne);
          this.submissionSteps[2].status = 'completed';
        }

        // Principal Contact #2
        this.principalContactTwo['Organization ID'] = this.company['Organization ID'];
        if (!this.principalContactTwo['Contact ID']) {
          // Submit Principal Contact #2, if we didn't select it
          const { data: principalContactTwoResult } = await createContact(this.principalContactTwo);
          this.submissionSteps[3].status = 'completed';
          if (principalContactTwoResult.messages[0].message !== 'OK') {
            this.submissionSteps[3].status = 'error';
            this.$toast.error('An error occurred while trying to create the second principal contact');
            this.processingErrorMessage = principalContactTwoResult.messages[0].message;
            return;
          }
        } else {
          // Update Principal Contact #2, if we selected it
          await updateContact(this.principalContactTwo['Contact ID'], this.principalContactTwo);
          this.submissionSteps[3].status = 'completed';
        }

        // QCP Contact Test Info
        const { data: qcpContactTestInfoResult } = await createTestContact({
          contact_id: this.primaryContact['Contact ID'],
          organization_id: this.company['Organization ID'],
          participant_status: 'Waiting for test/ref',
          qcp_code: 'AAM',
          sections_requested: this.computedLicensingSections.join('\n'),
          expiration_date: dayjs().add(1, 'year').format('MM/DD/YYYY'),
          application_date: dayjs().format('MM/DD/YYYY'),
        });
        this.submissionSteps[4].status = 'completed';
        if (qcpContactTestInfoResult.messages[0].message !== 'OK') {
          this.submissionSteps[4].status = 'error';
          this.$toast.error('An error occurred while trying to create the QCP contact test information');
          this.processingErrorMessage = qcpContactTestInfoResult.messages[0].message;
          return;
        }

        // Submit transaction
        try {
          await axios.post(`/api/transactions/${this.newSubmissionDetails.transactionId}/process?filemakerIdentifier=${this.company['Organization ID']}`);
          this.submissionSteps[5].status = 'completed';
        } catch (error) {
          this.submissionSteps[5].status = 'error';
          this.$toast.error('An error occurred while trying to process the transaction');
          this.processingErrorMessage = error.response.data[0];
          return;
        }

        // Create user account
        try {
          await axios.post('/api/qcp-users', {
            email: this.primaryContact.Email,
            username: this.primaryContact['Contact ID'].toUpperCase(),
            password: this.company['Organization ID'].toUpperCase(),
            firstName: this.primaryContact['Name First Name'],
            lastName: this.primaryContact['Name Last Name'],
            phoneNumber: this.primaryContact.Phone,
            roles: ['QCP LIAISON', 'PARTICIPANT'],
          });
        } catch (error) {
          this.submissionSteps[6].status = 'error';
          this.$toast.error('An error occurred while trying to create the user account');
          this.processingErrorMessage = error.response.data[0];
          return;
        }

        // Update the info in the local DB
        this.newSubmissionDetails.status = RequestStatus.Completed;
        await update(this.submissionId, this.newSubmissionDetails);
        this.$parent.events.updateRevisedInfo(this.newSubmissionDetails);

        // Wrap up
        this.$parent.close();
        this.$toast.success('The application has finished processing and marked as completed');
      },
    },
  };
</script>